const LIGHT_INK = '#ffffff';
const DARK_INK = '#040f16';
const channels = (hex: string): readonly [number, number, number] => [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
];

const toHex = (value: number): string =>
    Math.round(Math.max(0, Math.min(255, value)))
        .toString(16)
        .padStart(2, '0');

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Linear sRGB mix of two hex colours; `amount` 0 keeps `from`, 1 lands on `to`. Non-hex input passes through. */
export const mixHex = (from: string, to: string, amount: number): string => {
    if (!HEX_COLOR.test(from) || !HEX_COLOR.test(to)) {
        return from;
    }

    const a = channels(from);
    const b = channels(to);

    return `#${toHex(a[0] + (b[0] - a[0]) * amount)}${toHex(a[1] + (b[1] - a[1]) * amount)}${toHex(a[2] + (b[2] - a[2]) * amount)}`;
};

/** A lit edge of the fill: the same hue pulled toward the light ink. */
export const lightenHex = (hex: string, amount: number): string => mixHex(hex, LIGHT_INK, amount);

/** A shaded edge of the fill: the same hue pulled toward the dark ink. */
export const darkenHex = (hex: string, amount: number): string => mixHex(hex, DARK_INK, amount);

type Rgb = readonly [number, number, number];

const RGB_COLOR = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+)(%?))?/i;

type Paint = { readonly alpha: number; readonly rgb: Rgb };

/**
 * The channels and own opacity of a `#rrggbb`, `rgb()` or `rgba()` colour, as semantic tokens resolve; undefined for
 * anything else.
 */
const parseColor = (color: string): Paint | undefined => {
    const value = color.trim();
    if (HEX_COLOR.test(value)) return { alpha: 1, rgb: channels(value) };
    const match = RGB_COLOR.exec(value);
    if (match === null) return undefined;
    const alpha = match[4] === undefined ? 1 : Number(match[4]) / (match[5] === '%' ? 100 : 1);

    return { alpha, rgb: [Number(match[1]), Number(match[2]), Number(match[3])] };
};

const blend = (under: Rgb, over: Rgb, alpha: number): Rgb => [
    under[0] + (over[0] - under[0]) * alpha,
    under[1] + (over[1] - under[1]) * alpha,
    under[2] + (over[2] - under[2]) * alpha,
];

const linear = (channel: number): number => {
    const value = channel / 255;

    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = ([red, green, blue]: Rgb): number =>
    0.2126 * linear(red) + 0.7152 * linear(green) + 0.0722 * linear(blue);

const contrast = (left: number, right: number): number =>
    (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05);

const LIGHT_INK_LUMINANCE = relativeLuminance(channels(LIGHT_INK));
const DARK_INK_LUMINANCE = relativeLuminance(channels(DARK_INK));

/**
 * The light or dark ink that contrasts more with paint laid in layers, such as a fill at some opacity over the plot.
 * Each layer covers the ones before it by its `alpha` times its colour's own; a colour it cannot read is skipped.
 */
export const readableInk = (layers: readonly { readonly alpha: number; readonly color: string }[]): string => {
    let surface: Rgb | undefined;
    for (const { alpha, color } of layers) {
        const over = parseColor(color);
        if (over !== undefined) {
            surface = surface === undefined ? over.rgb : blend(surface, over.rgb, alpha * over.alpha);
        }
    }
    if (surface === undefined) return DARK_INK;
    const luminance = relativeLuminance(surface);

    return contrast(luminance, LIGHT_INK_LUMINANCE) > contrast(luminance, DARK_INK_LUMINANCE) ? LIGHT_INK : DARK_INK;
};
